import { client, gql } from 'data-provider';
import { IAddress, ISupplier } from 'interfaces';

interface GetAllSupplierParam {
  searchString?: string;
  start?: number;
  limit?: number;
}

export const getAllSupplier = async (queryParam?: GetAllSupplierParam) => {
  const where = { query: '' };
  if (queryParam?.searchString) {
    where.query = queryParam.searchString;
  }

  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      suppliers(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          name
          email
          supplierCode
          mobileNumber
          supplierPhoto {
            avatar {
              url
              thumbUrl
            }
          }
          address {
            id
            street
            area
            city
            country
            zip
          }
          supplierTags {
            tag {
              id
              name
              description
            }
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: queryParam && queryParam.start ? queryParam.start : 1,
    limit: queryParam && queryParam.limit ? queryParam.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.suppliers as {
    data: ISupplier[];
    page: number;
    size: number;
    total: number;
  };
};

export const getSupplier = async (supplierId: string) => {
  const query = `
    query($id: ID!) {
      supplier(id: $id) {
        id
        pointOfContacts {
          id
          name
        }
        address {
          id
          street
          area
          city
          country
          zip
        }
      }
    }
  `;

  const variables = { id: supplierId };
  const data = await client.request(query, variables);
  return data.supplier as any;
};

export interface ISupplierFile {
  name: string;
  supplierCode: string;
  email?: string;
  mobileNumber?: string;
  address?: Partial<Omit<IAddress, 'id'>>;
}

export const importSupplier = async (suppliers: ISupplierFile[]) => {
  const importSupplier = `
    mutation ($input: createInputCSVBulkSupplierInput) {
      importCSVSupplier(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data: suppliers },
  };
  const createdData = await client.request(importSupplier, variables);
  return createdData;
};

export const getSuppliersDropdown = async () => {
  const query = gql`
    query {
      supplierDropdown {
        id
        name
        email
        mobileNumber
        supplierCode
      }
    }
  `;

  const data = await client.request(query);
  return data.supplierDropdown as Omit<
    ISupplier,
    'address' | 'pointOfContacts' | 'supplierPhoto' | 'supplierTags' | 'payMethods'
  >[];
};

export const getSuppliersExport = async () => {
  const query = gql`
    query {
      supplierDropdown {
        id
        name
        email
        mobileNumber
        supplierCode
        address {
          id
          street
          area
          city
          country
        }
        supplierTags {
          id
          tag {
            name
          }
        }
      }
    }
  `;

  const data = await client.request(query);
  return data.supplierDropdown as ISupplier[];
};

export const checkSupplierNameUnique = async (name: string) => {
  const query = gql`
    query ($name: String) {
      isSupplierNameUnique(name: $name)
    }
  `;

  const variables = {
    name,
  };
  const data = await client.request(query, variables);
  return data.isSupplierNameUnique as boolean;
};

export interface ISupplierReportItem {
  lotId: string;
  productVariantId: string;
  productName: string;
  productImage?: string;
  sku: string;
  quantityProcured: number;
  quantitySold: number;
  quantityReturned: number;
  quantityInStock: number;
  supplierPrice: number;
  totalSalesValue: number;
  netSoldQuantity: number;
  netSalesValue: number;
  purchaseDate?: string;
}

export interface ISupplierReportResponse {
  data: ISupplierReportItem[];
  total: number;
}

export const getSupplierReport = async (variables: {
  supplierId?: string;
  startDate?: string;
  endDate?: string;
  start?: number;
  limit?: number;
  query?: string;
}) => {
  const query = gql`
    query ($supplierId: String, $startDate: String, $endDate: String, $start: Int, $limit: Int, $query: String) {
      supplierReport(
        supplierId: $supplierId
        startDate: $startDate
        endDate: $endDate
        start: $start
        limit: $limit
        query: $query
      ) {
        data {
          lotId
          productVariantId
          productName
          productImage
          sku
          quantityProcured
          quantitySold
          quantityReturned
          quantityInStock
          supplierPrice
          totalSalesValue
          netSoldQuantity
          netSalesValue
          purchaseDate
        }
        total
      }
    }
  `;

  const data = await client.request(query, variables);
  return data.supplierReport as ISupplierReportResponse;
};

